import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  DollarSign,
  Inbox,
  Layers,
  List,
  Lock,
  Minus,
  Package,
  Plus,
  Search,
  Trash2,
  WifiOff,
  X,
  Zap,
} from "lucide-react";
import { BinPickerModal } from "@/components/BinPickerModal";
import { FormField, SelectField } from "@/components/FormField";
import { PrimaryButton } from "@/components/PrimaryButton";
import {
  calcGlobalCostPerCard,
  generateId,
  useApp,
  type Card,
  type CardCondition,
} from "@/context/AppContext";
import {
  CONDITION_FACTORS,
  conditionAdjust,
  fetchSetCards,
  getBulkCardPrice,
  getCardVariants,
  isBulkFlatPrice,
  mapFinish,
  mapRarity,
  PRICE_TYPE_LABELS,
  searchSets,
  type TcgSet,
  type TcgSetCard,
} from "@/features/tcgApi";
import {
  invalidateLotCardStats,
  useLotCardStats,
} from "@/features/useLotCardStats";

export const Route = createFileRoute("/inventory/bulk")({
  component: InventoryBulkPage,
});

const CONDITIONS: CardCondition[] = ["NM", "LP", "MP", "HP", "Damaged"];

const CONFIRM_VARIANTS = [
  { type: "normal", label: "Normal" },
  { type: "holofoil", label: "Holo" },
  { type: "reverseHolofoil", label: "Rev. Holo" },
] as const;

const FLAT_VARIANTS = [
  { type: "normal", label: "Normal" },
  { type: "reverseHolofoil", label: "Rev. Holo" },
] as const;

const VARIANT_LOCK_OPTIONS: Array<{
  type: string;
  label: string;
  description: string;
}> = [
  { type: "normal", label: "Normal", description: "Standard print" },
  {
    type: "reverseHolofoil",
    label: "Rev. Holo",
    description: "Reverse holographic",
  },
  { type: "holofoil", label: "Holo", description: "Holographic foil" },
];

const RARITY_FILTERS = [
  { key: "all", label: "All", match: (_c: TcgSetCard) => true },
  {
    key: "common",
    label: "Common",
    match: (c: TcgSetCard) => (c.rarity ?? "").toLowerCase() === "common",
  },
  {
    key: "uncommon",
    label: "Uncommon",
    match: (c: TcgSetCard) => (c.rarity ?? "").toLowerCase() === "uncommon",
  },
  {
    key: "trainer",
    label: "Trainer",
    match: (c: TcgSetCard) =>
      c.supertype?.toLowerCase() === "trainer" ||
      !!(c.rarity ?? "").toLowerCase().includes("trainer"),
  },
  {
    key: "energy",
    label: "Energy",
    match: (c: TcgSetCard) =>
      c.supertype?.toLowerCase() === "energy" ||
      !!(c.rarity ?? "").toLowerCase().includes("energy"),
  },
  {
    key: "holo",
    label: "Holo+",
    match: (c: TcgSetCard) => {
      const sup = c.supertype?.toLowerCase();
      if (sup === "trainer" || sup === "energy") return false;
      const l = (c.rarity ?? "").toLowerCase();
      return (
        l.includes("holo") ||
        l.includes("ultra") ||
        l.includes("secret") ||
        l.includes("rainbow") ||
        l.includes("gold") ||
        l.includes("radiant") ||
        l.includes("illustration") ||
        l.includes("hyper")
      );
    },
  },
  {
    key: "rare",
    label: "Rare",
    match: (c: TcgSetCard) => {
      const sup = c.supertype?.toLowerCase();
      if (sup === "trainer" || sup === "energy") return false;
      const l = (c.rarity ?? "").toLowerCase();
      return (
        l.includes("rare") &&
        !l.includes("holo") &&
        !l.includes("ultra") &&
        !l.includes("secret")
      );
    },
  },
] as const;

type RarityFilterKey = (typeof RARITY_FILTERS)[number]["key"];

interface SelectedCard {
  card: TcgSetCard;
  variants: Map<string, number>;
}

type Step = "set" | "variant" | "cards" | "confirm";
type CardMode = "browse" | "quick";
type QuickFlash = { ok: boolean; msg: string };

function compareCardNumber(a: string, b: string): number {
  const numA = parseInt(a, 10);
  const numB = parseInt(b, 10);
  if (!isNaN(numA) && !isNaN(numB) && numA !== numB) return numA - numB;
  if (!isNaN(numA) && isNaN(numB)) return -1;
  if (!isNaN(numB) && isNaN(numA)) return 1;
  return a.localeCompare(b);
}

function sortCards(cards: TcgSetCard[]): TcgSetCard[] {
  return [...cards].sort((a, b) => compareCardNumber(a.number, b.number));
}

function filterSets(sets: TcgSet[], query: string): TcgSet[] {
  const q = query.trim().toLowerCase();
  if (!q) return sets;
  return sets.filter(
    (s) =>
      s.name.toLowerCase().includes(q) ||
      s.series.toLowerCase().includes(q) ||
      s.id.toLowerCase().includes(q) ||
      (s.ptcgoCode != null && s.ptcgoCode.toLowerCase().includes(q)),
  );
}

function InventoryBulkPage() {
  const navigate = useNavigate();
  const { lots, addCards } = useApp();

  // ── Step tracking ─────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>("set");

  // ── Step 1: set picker ────────────────────────────────────────────────────
  const [setQuery, setSetQuery] = useState("");
  const [allSets, setAllSets] = useState<TcgSet[]>([]);
  const [setsLoaded, setSetsLoaded] = useState(false);
  const [setsLoading, setSetsLoading] = useState(false);
  const [setsError, setSetsError] = useState("");
  const [selectedSet, setSelectedSet] = useState<TcgSet | null>(null);

  const visibleSets = useMemo(
    () => (setsLoaded ? filterSets(allSets, setQuery) : []),
    [setsLoaded, allSets, setQuery],
  );

  const loadSets = useCallback(async () => {
    setSetsLoading(true);
    setSetsError("");
    try {
      const loaded = await searchSets("");
      setAllSets(loaded);
      setSetsLoaded(true);
    } catch {
      setSetsError("Could not load sets. Try again.");
    } finally {
      setSetsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!setsLoaded) loadSets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Step 2: variant lock ──────────────────────────────────────────────────
  const [sessionLock, setSessionLock] = useState<string | null>(null);

  // ── Step 3: card browser + quick entry ────────────────────────────────────
  const [setCards, setSetCards] = useState<TcgSetCard[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState("");
  const cardsAbortRef = useRef<AbortController | null>(null);
  const [rarityFilter, setRarityFilter] = useState<RarityFilterKey>("all");
  const [cardSearch, setCardSearch] = useState("");
  const [selected, setSelected] = useState<Map<string, SelectedCard>>(
    new Map(),
  );

  const [cardMode, setCardMode] = useState<CardMode>("quick");
  const [quickInput, setQuickInput] = useState("");
  const [quickFlash, setQuickFlash] = useState<QuickFlash | null>(null);
  const [quickNameMatches, setQuickNameMatches] = useState<TcgSetCard[]>([]);
  const [quickVariantPin, setQuickVariantPin] = useState<string | null>(null);
  const [quickExpandedCardId, setQuickExpandedCardId] = useState<string | null>(
    null,
  );
  const [lastUsedVariant, setLastUsedVariant] = useState<string | null>(null);
  const quickInputRef = useRef<HTMLInputElement>(null);
  const quickFlashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadSetCards = useCallback(async (setId: string) => {
    if (cardsAbortRef.current) cardsAbortRef.current.abort();
    const controller = new AbortController();
    cardsAbortRef.current = controller;
    const isActive = () => cardsAbortRef.current === controller;

    setCardsLoading(true);
    setCardsError("");
    setSetCards([]);
    setSelected(new Map());

    const timer = setTimeout(() => controller.abort(), 15_000);
    try {
      const result = await fetchSetCards(setId, controller.signal);
      if (!isActive()) return;
      setSetCards(sortCards(result));
    } catch (err) {
      if (!isActive()) return;
      const isTimeout = err instanceof Error && err.name === "AbortError";
      setCardsError(
        isTimeout
          ? "Request timed out. Check your connection and try again."
          : "Could not load cards for this set. Try again.",
      );
    } finally {
      clearTimeout(timer);
      if (isActive()) setCardsLoading(false);
    }
  }, []);

  const resetQuickEntryState = () => {
    setQuickInput("");
    setQuickFlash(null);
    setQuickExpandedCardId(null);
    setQuickNameMatches([]);
    setQuickVariantPin(null);
    if (quickFlashTimer.current) {
      clearTimeout(quickFlashTimer.current);
      quickFlashTimer.current = null;
    }
  };

  const handlePickSet = (set: TcgSet) => {
    setSelectedSet(set);
    resetQuickEntryState();
    setSetCards([]);
    setSelected(new Map());
    setStep("variant");
  };

  const handlePickLock = (variantType: string) => {
    setSessionLock(variantType);
    setQuickVariantPin(variantType);
    setStep("cards");
    setCardMode("quick");
    if (selectedSet) loadSetCards(selectedSet.id);
  };

  const filteredCards = useMemo(
    () =>
      setCards.filter((c) => {
        const rarityMatch =
          RARITY_FILTERS.find((f) => f.key === rarityFilter)?.match(c) ?? true;
        const searchMatch =
          !cardSearch.trim() ||
          c.name.toLowerCase().includes(cardSearch.toLowerCase()) ||
          c.number.includes(cardSearch);
        return rarityMatch && searchMatch;
      }),
    [setCards, rarityFilter, cardSearch],
  );

  const makeDefaultVariants = (): Map<string, number> =>
    new Map([["normal", 1]]);

  const toggleCard = (card: TcgSetCard) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(card.id)) next.delete(card.id);
      else next.set(card.id, { card, variants: makeDefaultVariants() });
      return next;
    });
  };

  const toggleVariant = (cardId: string, variantType: string) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const entry = next.get(cardId);
      if (!entry) return next;
      const newVariants = new Map(entry.variants);
      if (newVariants.has(variantType)) {
        if (newVariants.size === 1) return next;
        const removedQty = newVariants.get(variantType) ?? 1;
        newVariants.delete(variantType);
        if (removedQty > 1) {
          const firstKey = newVariants.keys().next().value as
            | string
            | undefined;
          if (firstKey !== undefined) {
            newVariants.set(
              firstKey,
              (newVariants.get(firstKey) ?? 1) + removedQty,
            );
          }
        }
      } else {
        newVariants.set(variantType, 1);
      }
      next.set(cardId, { ...entry, variants: newVariants });
      return next;
    });
  };

  const setVariantQty = (cardId: string, variantType: string, qty: number) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const entry = next.get(cardId);
      if (!entry) return next;
      const newVariants = new Map(entry.variants);
      newVariants.set(variantType, Math.max(1, qty));
      next.set(cardId, { ...entry, variants: newVariants });
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      filteredCards.forEach((c) => {
        if (!next.has(c.id))
          next.set(c.id, { card: c, variants: makeDefaultVariants() });
      });
      return next;
    });
  };

  const deselectAllFiltered = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      filteredCards.forEach((c) => next.delete(c.id));
      return next;
    });
  };

  // ── Quick Entry handlers ──────────────────────────────────────────────────
  const commitQuickCard = (
    card: TcgSetCard,
    variantType: string,
    allVariants: Array<{ type: string; label: string }>,
  ) => {
    const label =
      allVariants.find((v) => v.type === variantType)?.label ?? variantType;
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(card.id)) {
        const entry = next.get(card.id)!;
        const nv = new Map(entry.variants);
        nv.set(variantType, (nv.get(variantType) ?? 0) + 1);
        next.set(card.id, { ...entry, variants: nv });
      } else {
        next.set(card.id, { card, variants: new Map([[variantType, 1]]) });
      }
      return next;
    });
    setLastUsedVariant(variantType);
    setQuickFlash({
      ok: true,
      msg: `+1 ${card.name} #${card.number} (${label})`,
    });
    if (quickFlashTimer.current) clearTimeout(quickFlashTimer.current);
    quickFlashTimer.current = setTimeout(() => setQuickFlash(null), 1500);
    setQuickInput("");
    setQuickNameMatches([]);
    setTimeout(() => quickInputRef.current?.focus(), 30);
  };

  const processQuickCard = (card: TcgSetCard) => {
    const RENDERABLE = new Set<string>(CONFIRM_VARIANTS.map((v) => v.type));
    const isFlat = isBulkFlatPrice(card);
    let allVariants: Array<{ type: string; label: string }>;
    if (isFlat) {
      allVariants = [...FLAT_VARIANTS];
    } else {
      const priced = getCardVariants(card).filter((v) =>
        RENDERABLE.has(v.type),
      );
      allVariants = priced.length > 0 ? priced : [...CONFIRM_VARIANTS];
    }
    const hasMultiple = allVariants.length >= 2;

    if (selected.has(card.id)) {
      const entry = selected.get(card.id)!;
      const primaryKey = [...entry.variants.keys()][0] ?? allVariants[0].type;
      const currentQty = entry.variants.get(primaryKey) ?? 0;
      setVariantQty(card.id, primaryKey, currentQty + 1);
      setLastUsedVariant(primaryKey);
      setQuickFlash({
        ok: true,
        msg: `+1 ${card.name} #${card.number} (qty ${currentQty + 1})`,
      });
      if (quickFlashTimer.current) clearTimeout(quickFlashTimer.current);
      quickFlashTimer.current = setTimeout(() => setQuickFlash(null), 1500);
      setQuickInput("");
      setQuickNameMatches([]);
      setTimeout(() => quickInputRef.current?.focus(), 30);
      return;
    }

    const effectiveLock = quickVariantPin ?? sessionLock;
    if (hasMultiple && effectiveLock) {
      const valid = allVariants.find((v) => v.type === effectiveLock);
      if (valid) {
        commitQuickCard(card, effectiveLock, allVariants);
        return;
      }
    }

    if (hasMultiple) {
      const preselect =
        (lastUsedVariant &&
          allVariants.find((v) => v.type === lastUsedVariant)?.type) ||
        allVariants[0].type;
      commitQuickCard(card, preselect, allVariants);
      return;
    }

    commitQuickCard(card, allVariants[0].type, allVariants);
  };

  const handleQuickSubmit = () => {
    const raw = quickInput.trim();
    if (!raw) return;
    if (quickFlashTimer.current) clearTimeout(quickFlashTimer.current);

    // Comma-separated batch (e.g. "5,12,33")
    const tokens = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (tokens.length > 1) {
      const seen = new Map<string, number>();
      const failed: string[] = [];
      for (const num of tokens) {
        const c = setCards.find(
          (card) => card.number.toLowerCase() === num.toLowerCase(),
        );
        if (!c) {
          failed.push(num);
          continue;
        }
        seen.set(c.id, (seen.get(c.id) ?? 0) + 1);
      }
      setSelected((prev) => {
        const next = new Map(prev);
        for (const [cardId, delta] of seen) {
          const c = setCards.find((card) => card.id === cardId)!;
          const defaultType = CONFIRM_VARIANTS[0].type;
          if (next.has(cardId)) {
            const entry = next.get(cardId)!;
            const primaryKey = [...entry.variants.keys()][0] ?? defaultType;
            const newVariants = new Map(entry.variants);
            newVariants.set(
              primaryKey,
              (newVariants.get(primaryKey) ?? 0) + delta,
            );
            next.set(cardId, { card: c, variants: newVariants });
          } else {
            next.set(cardId, {
              card: c,
              variants: new Map([[defaultType, delta]]),
            });
          }
        }
        return next;
      });
      const added = seen.size;
      let msg =
        added > 0 ? `✓ Added ${added} card type${added !== 1 ? "s" : ""}` : "";
      if (failed.length > 0)
        msg += (msg ? " · " : "") + `Not found: #${failed.join(", #")}`;
      setQuickFlash({ ok: failed.length === 0, msg: msg || "Nothing added" });
      quickFlashTimer.current = setTimeout(() => setQuickFlash(null), 2000);
      setQuickInput("");
      setTimeout(() => quickInputRef.current?.focus(), 30);
      return;
    }

    // Single number/name entry
    const num = tokens[0];
    let card = setCards.find(
      (c) => c.number.toLowerCase() === num.toLowerCase(),
    );
    if (!card) {
      const nameLower = num.toLowerCase();
      const nameMatches = setCards.filter((c) =>
        c.name.toLowerCase().includes(nameLower),
      );
      if (nameMatches.length === 0) {
        setQuickFlash({ ok: false, msg: `No card found for "${num}"` });
        setQuickInput("");
        setQuickNameMatches([]);
        quickFlashTimer.current = setTimeout(() => setQuickFlash(null), 2000);
        setTimeout(() => quickInputRef.current?.focus(), 30);
        return;
      }
      if (nameMatches.length === 1) {
        card = nameMatches[0];
      } else {
        setQuickNameMatches(nameMatches);
        setQuickFlash(null);
        return;
      }
    }
    processQuickCard(card);
  };

  const removeFromQuick = (cardId: string) => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.delete(cardId);
      return next;
    });
    if (quickExpandedCardId === cardId) setQuickExpandedCardId(null);
  };

  // ── Derived totals ────────────────────────────────────────────────────────
  const selectedCount = useMemo(
    () =>
      [...selected.values()].reduce(
        (s, e) => s + [...e.variants.values()].reduce((vs, q) => vs + q, 0),
        0,
      ),
    [selected],
  );

  const { quickSubtotal, hasQuickSubtotal } = useMemo(() => {
    let total = 0;
    let hasPriced = false;
    for (const { card, variants } of selected.values()) {
      const isFlat = isBulkFlatPrice(card);
      const flatPrice = getBulkCardPrice(card);
      for (const [variantType, qty] of variants.entries()) {
        let price: number | null;
        if (isFlat) price = flatPrice;
        else {
          const market = card.tcgplayer?.prices?.[variantType]?.market;
          price = market != null ? market : flatPrice;
        }
        if (price != null) {
          hasPriced = true;
          total += price * qty;
        }
      }
    }
    return { quickSubtotal: total, hasQuickSubtotal: hasPriced };
  }, [selected]);

  const allFilteredSelected =
    filteredCards.length > 0 && filteredCards.every((c) => selected.has(c.id));

  // ── Step 4: confirm fields ────────────────────────────────────────────────
  const [condition, setCondition] = useState<CardCondition>("NM");
  const [sourceLotId, setSourceLotId] = useState("");
  const [costOverride, setCostOverride] = useState("");
  const [costAutoFilled, setCostAutoFilled] = useState(false);
  const [costGlobalFilled, setCostGlobalFilled] = useState(false);
  const [storageLocation, setStorageLocation] = useState("");
  const [binPickerVisible, setBinPickerVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  // Server-side singles roll-up (TNK-155 phase E) — replaces the in-memory
  // `existingCards.filter(c => c.sourceLotId === id)` reduce that broke
  // once phase D made cards lazy-loaded.
  const { stats: lotCardStats } = useLotCardStats(sourceLotId || null);

  const getLotStats = useCallback(
    (lotId: string) => {
      const lot = lots.find((l) => l.id === lotId);
      if (!lot) return null;
      const totalCost = lot.purchaseCost + lot.shippingCost + lot.taxFees;
      const sortedCount = lot.sortingBins.reduce(
        (s, b) => s + (b.count || 0),
        0,
      );
      const totalCards = sortedCount > 0 ? sortedCount : lot.estimatedCards;
      const singlesCount = lotCardStats?.singlesCount ?? 0;
      const costAllocated = lotCardStats?.costAllocated ?? 0;
      const perCardCost = totalCards > 0 ? totalCost / totalCards : 0;
      return {
        lot,
        totalCost,
        totalCards,
        singlesCount,
        costAllocated,
        perCardCost,
      };
    },
    [lots, lotCardStats],
  );

  const lotStats = sourceLotId ? getLotStats(sourceLotId) : null;
  const globalCPC = useMemo(() => calcGlobalCostPerCard(lots), [lots]);

  useEffect(() => {
    if (!sourceLotId) {
      if (costAutoFilled || costGlobalFilled || !costOverride.trim()) {
        setCostAutoFilled(false);
        if (globalCPC > 0) {
          setCostOverride(globalCPC.toFixed(4));
          setCostGlobalFilled(true);
        } else {
          setCostOverride("");
          setCostGlobalFilled(false);
        }
      }
      return;
    }
    setCostGlobalFilled(false);
    const stats = getLotStats(sourceLotId);
    if (!stats || stats.perCardCost <= 0) return;
    if (!costOverride || costAutoFilled || costGlobalFilled) {
      setCostOverride(stats.perCardCost.toFixed(4));
      setCostAutoFilled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceLotId]);

  useEffect(() => {
    if (!sourceLotId && globalCPC > 0 && !costOverride) {
      setCostOverride(globalCPC.toFixed(4));
      setCostGlobalFilled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const perCardCost = costOverride ? parseFloat(costOverride) || 0 : 0;
  const totalCostToAllocate = perCardCost * selectedCount;

  const selectedEntries = useMemo(
    () =>
      [...selected.values()].sort((a, b) =>
        compareCardNumber(a.card.number, b.card.number),
      ),
    [selected],
  );

  const lotOptions = ["", ...lots.map((l) => l.id)];
  const lotLabels = [
    "None",
    ...lots.map((l) => {
      const date = new Date(l.purchaseDate).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return l.seller ? `${l.seller} · ${date}` : date;
    }),
  ];

  const handleSave = () => {
    if (selected.size === 0) return;
    setSaving(true);
    const now = new Date().toISOString();
    const newCards: Card[] = [];
    // No auto-image attach from pokemontcg.io stock catalog — those are not
    // photos of the user's physical copies. Inventory shows the actual card
    // photo (uploaded manually or via an import that ships URLs) or nothing.

    for (const { card, variants } of selectedEntries) {
      const isFlat = isBulkFlatPrice(card);
      const flatPrice = getBulkCardPrice(card) ?? 0;

      for (const [variantType, quantity] of variants.entries()) {
        let marketPrice: number;
        let variantLabel: string;

        if (isFlat) {
          marketPrice = flatPrice;
          variantLabel = PRICE_TYPE_LABELS[variantType] ?? variantType;
        } else {
          marketPrice =
            card.tcgplayer?.prices?.[variantType]?.market ?? flatPrice;
          variantLabel = PRICE_TYPE_LABELS[variantType] ?? variantType;
        }

        const adjPrice =
          marketPrice > 0 ? conditionAdjust(marketPrice, condition) : 0;

        // Prefer the card's own set if cross-set picker drift ever applies; else
        // fall back to the picked set.
        const persistedSetCode = (selectedSet?.id ?? "").toUpperCase();
        const persistedSetName = selectedSet?.name ?? "";

        const id = generateId("SKU");
        newCards.push({
          id,
          name: card.name,
          game: "Pokemon",
          setName: persistedSetName,
          setCode: persistedSetCode,
          cardNumber: card.number,
          rarity: mapRarity(
            card.rarity,
            card.supertype,
            card.subtypes,
            card.hp,
          ),
          finish: mapFinish(
            card.rarity,
            card.supertype,
            card.subtypes,
            card.hp,
          ),
          variant: variantLabel,
          condition,
          quantity,
          sourceLotId,
          costBasis: perCardCost,
          marketPrice,
          expectedSalePrice: adjPrice,
          storageLocation,
          listingStatus: "Unlisted",
          saleVenue: "",
          notes: "",
          createdAt: now,
          tcgSupertype: card.supertype,
          tcgSubtypes: card.subtypes,
          tcgTypes: card.types,
          tcgHp: card.hp,
          tcgArtist: card.artist,
          tcgPokedexNumbers: card.nationalPokedexNumbers,
          tcgSeries: card.set?.series,
        });
      }
    }

    void addCards(newCards);
    // Bust the per-lot singles cache so a follow-up form load reflects the
    // new rows.
    if (sourceLotId) invalidateLotCardStats(sourceLotId);
    setSaving(false);
    navigate({ to: "/inventory" });
  };

  // Auto-focus the quick input whenever quick mode becomes active.
  useEffect(() => {
    if (step === "cards" && cardMode === "quick") {
      setTimeout(() => quickInputRef.current?.focus(), 100);
    }
  }, [step, cardMode]);

  // ── STEP 1: Set picker ────────────────────────────────────────────────────
  if (step === "set") {
    return (
      <section className="flex h-full flex-col">
        <header className="space-y-3 border-b border-border pb-3">
          <div className="flex items-center justify-between">
            <Link
              to="/inventory"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" /> Cancel
            </Link>
            <h1 className="text-lg font-semibold">Bulk Add Cards</h1>
            <span className="w-16" />
          </div>
          <p className="text-xs text-muted-foreground">
            Step 1 of 3 — Choose a Set
          </p>
          <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2.5">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              value={setQuery}
              onChange={(e) => setSetQuery(e.target.value)}
              placeholder="Search sets (e.g. Base Set, Scarlet…)"
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              autoComplete="off"
            />
            {setQuery.length > 0 && (
              <button
                type="button"
                onClick={() => setSetQuery("")}
                aria-label="Clear search"
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto py-4">
          {setsLoading ? (
            <div className="flex flex-col items-center gap-3 py-10 text-sm text-muted-foreground">
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Loading sets…
            </div>
          ) : setsError ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <WifiOff className="h-7 w-7 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{setsError}</p>
              <PrimaryButton label="Retry" onClick={loadSets} />
            </div>
          ) : !setsLoaded ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <Layers className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-semibold text-foreground">
                Find a Set
              </p>
              <p className="text-xs text-muted-foreground">
                Type a set name above, or load the recent sets list.
              </p>
              <PrimaryButton
                label="Browse Sets"
                onClick={loadSets}
                leadingIcon={<List className="h-4 w-4" />}
              />
            </div>
          ) : visibleSets.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <Search className="h-7 w-7 text-muted-foreground" />
              <p className="text-sm font-semibold text-foreground">
                No Sets Found
              </p>
              <p className="text-xs text-muted-foreground">
                Try a different search term.
              </p>
              <PrimaryButton
                label="Clear Search"
                onClick={() => setSetQuery("")}
                variant="secondary"
              />
            </div>
          ) : (
            <ul className="space-y-2">
              {visibleSets.map((set) => (
                <li key={set.id}>
                  <button
                    type="button"
                    onClick={() => handlePickSet(set)}
                    className="flex w-full items-center gap-3 rounded-md border border-border bg-card p-3 text-left transition-colors hover:bg-card/80"
                  >
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-foreground">
                        {set.name}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {set.series} · {set.releaseDate} · {set.total} cards
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    );
  }

  // ── STEP 2: Variant lock ──────────────────────────────────────────────────
  if (step === "variant") {
    return (
      <section className="flex h-full flex-col">
        <header className="space-y-2 border-b border-border pb-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                setStep("set");
                resetQuickEntryState();
              }}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <div className="min-w-0 flex-1 px-3 text-center">
              <h1 className="truncate text-base font-semibold">
                {selectedSet?.name}
              </h1>
              <p className="text-[11px] text-muted-foreground">
                {selectedSet?.series} · {selectedSet?.total} cards
              </p>
            </div>
            <span className="w-16" />
          </div>
          <p className="text-xs text-muted-foreground">
            Step 2 of 3 — Default Print Type
          </p>
        </header>

        <div className="flex-1 overflow-y-auto py-4">
          <p className="mb-3 text-sm text-muted-foreground">
            Which print type are you adding? Card numbers you enter will be
            added with this variant by default.
          </p>
          <div className="space-y-2">
            {VARIANT_LOCK_OPTIONS.map((opt) => {
              const isSelected = sessionLock === opt.type;
              return (
                <button
                  key={opt.type}
                  type="button"
                  onClick={() => handlePickLock(opt.type)}
                  className={`flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors ${
                    isSelected
                      ? "border-primary bg-primary/10"
                      : "border-border bg-card hover:bg-card/80"
                  }`}
                >
                  <div className="flex-1">
                    <div
                      className={`text-sm font-semibold ${
                        isSelected ? "text-primary" : "text-foreground"
                      }`}
                    >
                      {opt.label}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {opt.description}
                    </div>
                  </div>
                  {isSelected && <Check className="h-4 w-4 text-primary" />}
                </button>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  // ── STEP 3: Card browser / quick entry ────────────────────────────────────
  if (step === "cards") {
    return (
      <section className="flex h-full flex-col">
        <header className="space-y-3 border-b border-border pb-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep("set")}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <div className="min-w-0 flex-1 px-3 text-center">
              <h1 className="truncate text-base font-semibold">
                {selectedSet?.name ?? "Bulk Add"}
              </h1>
              <p className="text-[11px] text-muted-foreground">
                {selectedSet?.series} · {selectedSet?.total} cards
              </p>
            </div>
            <span className="rounded-full bg-secondary/15 px-2 py-0.5 text-[11px] font-semibold text-secondary">
              {selected.size} sel.
            </span>
          </div>

          <div className="flex items-center gap-1 rounded-md bg-muted p-1">
            <button
              type="button"
              onClick={() => setCardMode("browse")}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                cardMode === "browse"
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <List className="h-3.5 w-3.5" /> Browse
            </button>
            <button
              type="button"
              onClick={() => setCardMode("quick")}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                cardMode === "quick"
                  ? "bg-card text-secondary shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Zap className="h-3.5 w-3.5" /> Type
            </button>
          </div>

          {cardMode === "browse" && (
            <>
              <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  type="search"
                  value={cardSearch}
                  onChange={(e) => setCardSearch(e.target.value)}
                  placeholder="Filter by name or #"
                  className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                  autoComplete="off"
                />
                {cardSearch.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setCardSearch("")}
                    aria-label="Clear filter"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1 pb-1">
                {RARITY_FILTERS.map((f) => {
                  const count =
                    f.key === "all"
                      ? setCards.length
                      : setCards.filter((c) => f.match(c)).length;
                  const active = rarityFilter === f.key;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setRarityFilter(f.key)}
                      className={`shrink-0 rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors ${
                        active
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      }`}
                    >
                      {f.label}
                      {count > 0 ? ` (${count})` : ""}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </header>

        <div className="flex-1 overflow-y-auto py-3">
          {cardsLoading ? (
            <div className="flex flex-col items-center gap-3 py-10 text-sm text-muted-foreground">
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Loading cards…
            </div>
          ) : cardsError ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <WifiOff className="h-7 w-7 text-muted-foreground" />
              <p className="text-sm font-semibold text-foreground">
                Couldn't Load Cards
              </p>
              <p className="text-xs text-muted-foreground">{cardsError}</p>
              <PrimaryButton
                label="Try Again"
                onClick={() => selectedSet && loadSetCards(selectedSet.id)}
              />
            </div>
          ) : cardMode === "browse" ? (
            <BrowseMode
              filteredCards={filteredCards}
              selected={selected}
              allFilteredSelected={allFilteredSelected}
              onToggleCard={toggleCard}
              onToggleVariant={toggleVariant}
              onSetVariantQty={setVariantQty}
              onSelectAll={selectAllFiltered}
              onDeselectAll={deselectAllFiltered}
              onClearFilters={() => {
                setCardSearch("");
                setRarityFilter("all");
              }}
              hasFilters={cardSearch.trim() !== "" || rarityFilter !== "all"}
            />
          ) : (
            <QuickEntryMode
              selected={selected}
              selectedCount={selectedCount}
              quickInput={quickInput}
              quickFlash={quickFlash}
              quickNameMatches={quickNameMatches}
              quickExpandedCardId={quickExpandedCardId}
              quickVariantPin={quickVariantPin}
              sessionLock={sessionLock}
              quickSubtotal={quickSubtotal}
              hasQuickSubtotal={hasQuickSubtotal}
              quickInputRef={quickInputRef}
              onChangeInput={(v) => {
                setQuickInput(v);
                if (quickNameMatches.length > 0) setQuickNameMatches([]);
              }}
              onSubmit={handleQuickSubmit}
              onSelectName={(c) => processQuickCard(c)}
              onPickPin={(v) => setQuickVariantPin(v)}
              onExpand={(id) =>
                setQuickExpandedCardId(quickExpandedCardId === id ? null : id)
              }
              onSetVariantQty={setVariantQty}
              onToggleVariant={toggleVariant}
              onRemove={removeFromQuick}
            />
          )}
        </div>

        {selected.size > 0 && (
          <div className="flex items-center justify-between gap-3 border-t border-border bg-card px-4 py-3">
            <div className="text-sm font-medium text-foreground">
              {selected.size} card{selected.size !== 1 ? "s" : ""} selected
              {selectedCount !== selected.size
                ? ` · ${selectedCount} total qty`
                : ""}
            </div>
            <PrimaryButton
              label="Next →"
              variant="secondary"
              onClick={() => setStep("confirm")}
            />
          </div>
        )}
      </section>
    );
  }

  // ── STEP 4: Confirm & save ────────────────────────────────────────────────
  return (
    <section className="flex h-full flex-col">
      <header className="space-y-2 border-b border-border pb-3">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setStep("cards")}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <h1 className="text-lg font-semibold">Confirm & Add</h1>
          <span className="w-16" />
        </div>
        <p className="text-xs text-muted-foreground">
          Confirm cards — set condition & cost
        </p>
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto py-4">
        {/* Summary */}
        <div className="rounded-md bg-primary p-4 text-primary-foreground">
          <div className="text-xs opacity-75">{selectedSet?.name}</div>
          <div className="mt-1 text-lg font-bold text-secondary">
            {selected.size} card type{selected.size !== 1 ? "s" : ""}
          </div>
          <div className="text-sm">
            {selectedCount} total card{selectedCount !== 1 ? "s" : ""} to add to
            inventory
          </div>
        </div>

        {/* Storage */}
        <div className="rounded-md bg-card p-4">
          <div className="mb-2 text-[10px] font-medium tracking-wider text-muted-foreground">
            STORAGE
          </div>
          <button
            type="button"
            onClick={() => setBinPickerVisible(true)}
            className="flex w-full items-center gap-2 rounded-md bg-muted px-3 py-2.5 text-left hover:bg-muted/80"
          >
            <Inbox
              className={`h-4 w-4 ${storageLocation ? "text-secondary" : "text-muted-foreground"}`}
            />
            <span
              className={`flex-1 text-sm ${
                storageLocation ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {storageLocation || "Tap to assign a bin…"}
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        {/* Condition */}
        <div className="rounded-md bg-card p-4">
          <div className="mb-2 text-[10px] font-medium tracking-wider text-muted-foreground">
            CONDITION
          </div>
          <div className="flex flex-wrap gap-1.5">
            {CONDITIONS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCondition(c)}
                className={`flex flex-col items-center gap-0.5 rounded-md border px-3 py-2 text-xs transition-colors ${
                  condition === c
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-muted-foreground hover:border-primary/60"
                }`}
              >
                <span className="font-semibold">{c}</span>
                <span
                  className={
                    condition === c
                      ? "text-[10px] text-secondary"
                      : "text-[10px] text-muted-foreground"
                  }
                >
                  {(CONDITION_FACTORS[c] * 100).toFixed(0)}%
                </span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Market prices from TCGplayer are NM. Condition factor is applied to
            expected sale price.
          </p>
        </div>

        {/* Costing */}
        <div className="rounded-md bg-card p-4">
          <div className="mb-2 text-[10px] font-medium tracking-wider text-muted-foreground">
            COSTING
          </div>

          {lots.length > 0 && (
            <SelectField
              label="Source Lot (optional)"
              value={sourceLotId}
              options={lotOptions}
              optionLabels={lotLabels}
              onSelect={(v) => setSourceLotId(v)}
            />
          )}

          {lotStats && (
            <div className="mb-3 rounded-md border border-primary/30 bg-primary/5 p-2.5">
              <div className="flex items-center gap-2 text-xs font-medium text-primary">
                <Package className="h-3.5 w-3.5" />
                {lotStats.lot.seller || "Lot"} · $
                {lotStats.totalCost.toFixed(2)} total · {lotStats.totalCards}{" "}
                cards
              </div>
              <div className="mt-2 flex items-center justify-between gap-2 border-t border-border pt-2">
                <div className="flex-1">
                  <div className="text-[11px] text-muted-foreground">
                    Per-card cost basis
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    ${lotStats.totalCost.toFixed(2)} ÷ {lotStats.totalCards} = $
                    {lotStats.perCardCost.toFixed(4)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setCostOverride(lotStats.perCardCost.toFixed(4));
                    setCostAutoFilled(true);
                  }}
                  className="rounded-md bg-secondary/20 px-3 py-1.5 text-xs font-semibold text-secondary hover:bg-secondary/30"
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          <FormField
            label={
              costAutoFilled
                ? "Cost per Card (from lot)"
                : costGlobalFilled
                  ? "Cost per Card (portfolio avg)"
                  : "Cost per Card"
            }
            value={costOverride}
            onChangeText={(v) => {
              setCostOverride(v);
              setCostAutoFilled(false);
              setCostGlobalFilled(false);
            }}
            inputMode="decimal"
            placeholder="0.00"
            suffix="$"
          />
          {costGlobalFilled && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Using portfolio average · ${globalCPC.toFixed(4)}/card across all
              lots
            </p>
          )}

          {perCardCost > 0 && (
            <div className="mt-2 flex items-center gap-2 rounded-md bg-secondary/10 px-3 py-2 text-xs font-medium text-secondary">
              <DollarSign className="h-3.5 w-3.5" />${perCardCost.toFixed(4)} ×{" "}
              {selectedCount} cards = ${totalCostToAllocate.toFixed(2)} total
              cost allocated
            </div>
          )}
        </div>

        {/* Selected cards — variant split editor */}
        <div className="rounded-md bg-card p-4">
          <div className="mb-2 text-[10px] font-medium tracking-wider text-muted-foreground">
            SELECTED CARDS
          </div>
          <p className="mb-3 text-[11px] text-muted-foreground">
            Tap a variant chip to toggle it. Use +/- to split quantities.
          </p>
          <div className="space-y-3">
            {selectedEntries.map(({ card, variants }) => {
              const isFlat = isBulkFlatPrice(card);
              const totalQty = [...variants.values()].reduce(
                (s, q) => s + q,
                0,
              );
              const variantsToShow = isFlat ? FLAT_VARIANTS : CONFIRM_VARIANTS;
              const flatPrice = getBulkCardPrice(card);
              return (
                <div
                  key={card.id}
                  className="border-t border-border pt-3 first:border-t-0 first:pt-0"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-foreground">
                        {card.name}{" "}
                        <span className="text-muted-foreground">
                          #{card.number}
                        </span>
                      </div>
                      {card.rarity && (
                        <div className="text-[11px] text-muted-foreground">
                          {card.rarity}
                        </div>
                      )}
                    </div>
                    <span className="text-[11px] text-muted-foreground">
                      {totalQty} total
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {variantsToShow.map((v) => {
                      const isActive = variants.has(v.type);
                      const qty = variants.get(v.type) ?? 1;
                      const nmPrice = isFlat
                        ? null
                        : (card.tcgplayer?.prices?.[v.type]?.market ??
                          flatPrice);
                      const condPrice = isFlat
                        ? flatPrice
                        : nmPrice != null
                          ? conditionAdjust(nmPrice, condition)
                          : null;
                      return (
                        <div
                          key={v.type}
                          className={`flex items-stretch overflow-hidden rounded-md border ${
                            isActive
                              ? "border-secondary bg-secondary/15"
                              : "border-border bg-background"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleVariant(card.id, v.type)}
                            className="flex flex-col items-center px-2.5 py-1.5"
                          >
                            <span
                              className={`text-[11px] font-medium ${
                                isActive
                                  ? "text-secondary"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {v.label}
                            </span>
                            <span
                              className={`text-[10px] ${
                                isActive
                                  ? "text-secondary/80"
                                  : "text-muted-foreground/60"
                              }`}
                            >
                              {condPrice != null
                                ? `$${condPrice.toFixed(2)}`
                                : "—"}
                            </span>
                          </button>
                          {isActive && (
                            <div className="flex items-center gap-1.5 border-l border-border px-1.5">
                              <button
                                type="button"
                                onClick={() =>
                                  setVariantQty(card.id, v.type, qty - 1)
                                }
                                className="text-foreground hover:text-secondary"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="text-[11px] font-semibold text-foreground">
                                {qty}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  setVariantQty(card.id, v.type, qty + 1)
                                }
                                className="text-foreground hover:text-secondary"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <PrimaryButton
          label={`Add ${selectedCount} Card${selectedCount !== 1 ? "s" : ""} to Inventory`}
          onClick={handleSave}
          loading={saving}
          variant="secondary"
        />
      </div>

      <BinPickerModal
        open={binPickerVisible}
        value={storageLocation}
        onSelect={(name) => setStorageLocation(name)}
        onClose={() => setBinPickerVisible(false)}
      />
    </section>
  );
}

// ── Browse mode subview ────────────────────────────────────────────────────
interface BrowseModeProps {
  filteredCards: TcgSetCard[];
  selected: Map<string, SelectedCard>;
  allFilteredSelected: boolean;
  onToggleCard: (card: TcgSetCard) => void;
  onToggleVariant: (cardId: string, variantType: string) => void;
  onSetVariantQty: (cardId: string, variantType: string, qty: number) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onClearFilters: () => void;
  hasFilters: boolean;
}

function BrowseMode({
  filteredCards,
  selected,
  allFilteredSelected,
  onToggleCard,
  onToggleVariant,
  onSetVariantQty,
  onSelectAll,
  onDeselectAll,
  onClearFilters,
  hasFilters,
}: BrowseModeProps) {
  if (filteredCards.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <Search className="h-7 w-7 text-muted-foreground" />
        <p className="text-sm font-semibold text-foreground">
          {hasFilters ? "No Cards Match" : "No Cards Found"}
        </p>
        <p className="text-xs text-muted-foreground">
          {hasFilters
            ? "Try clearing the filter or search."
            : "This set returned no card data from the API."}
        </p>
        {hasFilters && (
          <PrimaryButton
            label="Clear Filters"
            onClick={onClearFilters}
            variant="secondary"
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="mb-2 flex items-center justify-between rounded-md bg-card px-3 py-2">
        <span className="text-xs text-muted-foreground">
          {filteredCards.length} showing
        </span>
        <button
          type="button"
          onClick={allFilteredSelected ? onDeselectAll : onSelectAll}
          className="text-xs font-medium text-secondary hover:underline"
        >
          {allFilteredSelected ? "Deselect all" : "Select all"}
        </button>
      </div>

      <ul className="space-y-2">
        {filteredCards.map((card) => {
          const isSelected = selected.has(card.id);
          const entry = selected.get(card.id);
          const displayPrice =
            card.tcgplayer?.prices?.["normal"]?.market ??
            getBulkCardPrice(card);

          return (
            <li
              key={card.id}
              className={`rounded-md border transition-colors ${
                isSelected
                  ? "border-secondary bg-secondary/10"
                  : "border-border bg-card hover:bg-card/80"
              }`}
            >
              <div
                role="button"
                tabIndex={0}
                onClick={() => onToggleCard(card)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggleCard(card);
                  }
                }}
                className="flex cursor-pointer items-center gap-3 p-3"
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded border ${
                    isSelected
                      ? "border-secondary bg-secondary text-primary"
                      : "border-border bg-transparent"
                  }`}
                >
                  {isSelected && <Check className="h-3 w-3" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-foreground">
                    {card.name}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    #{card.number}
                    {card.rarity ? ` · ${card.rarity}` : ""}
                  </div>
                </div>
                <div className="text-right">
                  {displayPrice != null ? (
                    <span className="text-sm font-semibold text-secondary">
                      ${displayPrice.toFixed(2)}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </div>
              </div>

              {isSelected && entry && (
                <div className="flex flex-wrap gap-1.5 border-t border-border bg-background/40 p-2.5">
                  {CONFIRM_VARIANTS.map((v) => {
                    const isActive = entry.variants.has(v.type);
                    const vQty = entry.variants.get(v.type) ?? 1;
                    return (
                      <div
                        key={v.type}
                        className={`flex items-stretch overflow-hidden rounded-md border ${
                          isActive
                            ? "border-secondary bg-secondary/15"
                            : "border-border bg-background"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleVariant(card.id, v.type);
                          }}
                          className="px-2.5 py-1.5 text-[11px] font-medium"
                        >
                          <span
                            className={
                              isActive
                                ? "text-secondary"
                                : "text-muted-foreground"
                            }
                          >
                            {v.label}
                          </span>
                        </button>
                        {isActive && (
                          <div className="flex items-center gap-1.5 border-l border-border px-1.5">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onSetVariantQty(card.id, v.type, vQty - 1);
                              }}
                              className="text-foreground hover:text-secondary"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="text-[11px] font-semibold text-foreground">
                              {vQty}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onSetVariantQty(card.id, v.type, vQty + 1);
                              }}
                              className="text-foreground hover:text-secondary"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

// ── Quick Entry mode subview ───────────────────────────────────────────────
interface QuickEntryProps {
  selected: Map<string, SelectedCard>;
  selectedCount: number;
  quickInput: string;
  quickFlash: QuickFlash | null;
  quickNameMatches: TcgSetCard[];
  quickExpandedCardId: string | null;
  quickVariantPin: string | null;
  sessionLock: string | null;
  quickSubtotal: number;
  hasQuickSubtotal: boolean;
  quickInputRef: React.RefObject<HTMLInputElement | null>;
  onChangeInput: (v: string) => void;
  onSubmit: () => void;
  onSelectName: (card: TcgSetCard) => void;
  onPickPin: (variantType: string) => void;
  onExpand: (cardId: string) => void;
  onSetVariantQty: (cardId: string, variantType: string, qty: number) => void;
  onToggleVariant: (cardId: string, variantType: string) => void;
  onRemove: (cardId: string) => void;
}

function QuickEntryMode({
  selected,
  selectedCount,
  quickInput,
  quickFlash,
  quickNameMatches,
  quickExpandedCardId,
  quickVariantPin,
  sessionLock,
  quickSubtotal,
  hasQuickSubtotal,
  quickInputRef,
  onChangeInput,
  onSubmit,
  onSelectName,
  onPickPin,
  onExpand,
  onSetVariantQty,
  onToggleVariant,
  onRemove,
}: QuickEntryProps) {
  const effectiveLock = quickVariantPin ?? sessionLock;
  const sortedEntries = [...selected.values()].sort((a, b) =>
    compareCardNumber(a.card.number, b.card.number),
  );

  return (
    <div className="space-y-3">
      {quickFlash && (
        <div
          className={`rounded-md border px-3 py-2 text-xs ${
            quickFlash.ok
              ? "border-secondary/40 bg-secondary/20 text-secondary"
              : "border-destructive/40 bg-destructive/10 text-destructive"
          }`}
        >
          {quickFlash.msg}
        </div>
      )}

      <div className="flex items-center gap-2 rounded-md border border-border bg-card p-2">
        <input
          ref={quickInputRef}
          value={quickInput}
          onChange={(e) =>
            onChangeInput(e.target.value.replace(/[^a-zA-Z0-9, ]/g, ""))
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSubmit();
            }
          }}
          placeholder="Card number, name, or 5,12,33…"
          className="flex-1 bg-transparent px-2 py-2 text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
          autoComplete="off"
          inputMode="text"
        />
        <button
          type="button"
          onClick={onSubmit}
          className="rounded-md bg-secondary p-2 text-primary hover:opacity-90"
          aria-label="Add card"
        >
          <Check className="h-4 w-4" />
        </button>
        {selectedCount > 0 && (
          <span className="rounded-md border border-secondary/40 bg-secondary/15 px-2 py-1 text-[11px] font-medium text-secondary">
            {selectedCount} card{selectedCount !== 1 ? "s" : ""}
            {hasQuickSubtotal ? ` · ~$${quickSubtotal.toFixed(2)}` : ""}
          </span>
        )}
      </div>

      {quickNameMatches.length > 0 && (
        <div className="space-y-1 rounded-md border border-border bg-card p-2">
          <div className="px-1 text-xs font-medium text-foreground">
            {quickNameMatches.length} cards match — pick one:
          </div>
          {quickNameMatches.slice(0, 12).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelectName(c)}
              className="flex w-full items-center gap-2 rounded-md border border-border p-2 text-left hover:bg-card/60"
            >
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                #{c.number}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-foreground">{c.name}</div>
                {c.rarity && (
                  <div className="text-[11px] text-muted-foreground">
                    {c.rarity}
                  </div>
                )}
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
          {quickNameMatches.length > 12 && (
            <div className="px-2 py-1 text-[11px] text-muted-foreground">
              and {quickNameMatches.length - 12} more — type more to narrow down
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <Lock className="h-3 w-3 text-muted-foreground" />
        {CONFIRM_VARIANTS.map((v) => {
          const isActive = effectiveLock === v.type;
          return (
            <button
              key={v.type}
              type="button"
              onClick={() => onPickPin(v.type)}
              className={`rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                isActive
                  ? "border-secondary bg-secondary/20 text-secondary"
                  : "border-border bg-muted text-muted-foreground hover:border-secondary/40"
              }`}
            >
              {v.label}
            </button>
          );
        })}
      </div>

      {selected.size === 0 && !quickFlash && quickNameMatches.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border p-6 text-center">
          <Zap className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-semibold text-foreground">
            Type a card number or name
          </p>
          <p className="text-xs text-muted-foreground">
            Enter the number from the bottom of each card, or part of a card
            name. Comma-separate to add several at once. Each entry auto-clears
            so you can move fast.
          </p>
        </div>
      )}

      {selected.size > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {selected.size} card type{selected.size !== 1 ? "s" : ""} ·{" "}
          {selectedCount} total
          {hasQuickSubtotal ? ` · ~$${quickSubtotal.toFixed(2)}` : ""} — tap a
          row to adjust · tap × to remove
        </p>
      )}

      <ul className="space-y-1.5">
        {sortedEntries.map((entry) => {
          const isExpanded = quickExpandedCardId === entry.card.id;
          const totalQty = [...entry.variants.values()].reduce(
            (s, q) => s + q,
            0,
          );
          const variantSummary = [...entry.variants.entries()]
            .map(([type, qty]) => {
              const label = PRICE_TYPE_LABELS[type] ?? type;
              return qty > 1 ? `${label} ×${qty}` : label;
            })
            .join(", ");
          const primary = [...entry.variants.entries()].sort(
            ([, a], [, b]) => b - a,
          )[0];
          const primaryKey = primary?.[0] ?? "normal";
          const primaryQty = primary?.[1] ?? 1;

          const handleInlineDecrement = () => {
            if (totalQty <= 1) {
              onRemove(entry.card.id);
              return;
            }
            onSetVariantQty(entry.card.id, primaryKey, primaryQty - 1);
          };

          return (
            <li
              key={entry.card.id}
              className={`rounded-md border ${
                isExpanded
                  ? "border-secondary/55 bg-secondary/5"
                  : "border-border bg-card"
              }`}
            >
              <div
                role="button"
                tabIndex={0}
                onClick={() => onExpand(entry.card.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onExpand(entry.card.id);
                  }
                }}
                className="flex cursor-pointer items-center gap-2 p-2.5"
              >
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  #{entry.card.number}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-foreground">
                    {entry.card.name}
                  </div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    {variantSummary}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInlineDecrement();
                    }}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-muted text-foreground hover:bg-muted/80"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="min-w-[1.5rem] text-center text-sm font-semibold text-secondary">
                    {totalQty}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetVariantQty(
                        entry.card.id,
                        primaryKey,
                        primaryQty + 1,
                      );
                    }}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-secondary/60 bg-secondary/15 text-secondary hover:bg-secondary/25"
                    aria-label="Increase quantity"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(entry.card.id);
                  }}
                  className="ml-1 text-muted-foreground hover:text-destructive"
                  aria-label="Remove card"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {isExpanded && (
                <div className="flex flex-wrap gap-1.5 border-t border-border p-2.5">
                  {CONFIRM_VARIANTS.map((v) => {
                    const isActive = entry.variants.has(v.type);
                    const vQty = entry.variants.get(v.type) ?? 1;
                    return (
                      <div
                        key={v.type}
                        className={`flex items-stretch overflow-hidden rounded-md border ${
                          isActive
                            ? "border-secondary bg-secondary/15"
                            : "border-border bg-background"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleVariant(entry.card.id, v.type);
                          }}
                          className="px-2.5 py-1.5 text-[11px] font-medium"
                        >
                          <span
                            className={
                              isActive
                                ? "text-secondary"
                                : "text-muted-foreground"
                            }
                          >
                            {v.label}
                          </span>
                        </button>
                        {isActive && (
                          <div className="flex items-center gap-1.5 border-l border-border px-1.5">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onSetVariantQty(
                                  entry.card.id,
                                  v.type,
                                  vQty - 1,
                                );
                              }}
                              className="text-foreground hover:text-secondary"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="text-[11px] font-semibold text-foreground">
                              {vQty}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onSetVariantQty(
                                  entry.card.id,
                                  v.type,
                                  vQty + 1,
                                );
                              }}
                              className="text-foreground hover:text-secondary"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
